import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import BASE_URL from "../../BASEURL";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';

export default function GallaryList() {
  const [gallaries, setGallaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingGallary, setEditingGallary] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [file, setFile] = useState(null);

  useEffect(() => {
    const fetchGallaries = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/galleries`);
        if (!response.ok) {
          throw new Error('Failed to fetch gallaries');
        }
        const data = await response.json();
        setGallaries(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGallaries();
  }, []);

  const handleDelete = async (id) => {
    setConfirmId(id);
    setShowConfirm(true);
  };

  const handleEdit = async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/api/galleries/${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch gallary');
      }
      const data = await response.json();
      setEditingGallary(data);
      setFile(null); // Reset file
      setShowEditModal(true);
    } catch (err) {
      setErrorMessage(err.message);
      setShowError(true);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(selectedFile.type)) {
        setErrorMessage("Please select a valid image (JPEG, PNG, GIF) or PDF file.");
        setShowError(true);
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);

    const formData = new FormData();
    formData.append('gallary_name', editingGallary.gallary_name);
    if (file) {
      formData.append('gallary_photo', file);
    }

    try {
      const response = await fetch(`${BASE_URL}/api/galleries/${editingGallary._id}`, {
        method: "PUT",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to update gallary");
      }

      // Update the gallary in state
      const updatedGallary = { ...editingGallary };
      if (file) {
        // Assuming the response includes the new photo path
        const data = await response.json();
        updatedGallary.gallary_photo = data.gallary_photo;
      }
      setGallaries(gallaries.map(gal => 
        gal._id === editingGallary._id ? updatedGallary : gal
      ));
      setShowEditModal(false);
      setEditingGallary(null);
      setFile(null);
    } catch (err) {
      setErrorMessage(err.message);
      setShowError(true);
    } finally {
      setUpdating(false);
    }
  };

  const confirmDelete = async () => {
    setShowConfirm(false);
    try {
      const response = await fetch(`${BASE_URL}/api/galleries/${confirmId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete gallary");
      }

      // Remove from state
      setGallaries(gallaries.filter(gal => gal._id !== confirmId));
    } catch (err) {
      setErrorMessage(err.message);
      setShowError(true);
    }
  };

  if (loading) return (
    <div className="p-6 flex justify-center items-center min-h-screen">
      <div className="text-gold text-xl">Loading...</div>
    </div>
  );
  if (error) return (
    <div className="p-6 flex justify-center items-center min-h-screen">
      <div className="text-red-500 text-xl">Error: {error}</div>
    </div>
  );

  return (
    <div className="p-6 bg-light-grey">

      {/* PAGE TITLE ROW */}
      <div className="flex flex-col gap-4 md:flex-row justify-between items-start md:items-center mb-8">
        <h1 className="text-3xl font-bold text-deep-black">
          Gallary List
        </h1>

        <Link
          to="/admin/add-gallary"
          className="bg-gold text-deep-black px-6 py-3 rounded-lg font-semibold hover:bg-gold-dark transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faPlus} /> Add New Gallary
        </Link>
      </div>

      {/* TABLE VIEW FOR DESKTOP */}
      <div className="hidden md:block bg-white rounded-2xl shadow-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-deep-black text-white">
            <tr>
              <th className="px-6 py-4 text-left">Gallary Name</th>
              <th className="px-6 py-4 text-left">Photo</th>
              <th className="px-6 py-4 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {gallaries.map((gal) => (
              <tr key={gal._id} className="border-b border-gray-200 hover:bg-light-grey transition-colors">
                <td className="px-6 py-4 font-semibold text-deep-black">{gal.gallary_name}</td>
                <td className="px-6 py-4">
                  {gal.gallary_photo ? (
                    <a href={`${gal.gallary_photo}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                      View File
                    </a>
                  ) : (
                    'No file'
                  )}
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex justify-center gap-3">
                    <button
                      onClick={() => handleEdit(gal._id)}
                      className="bg-gold text-deep-black px-3 py-2 rounded-lg hover:bg-gold-dark transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button
                      onClick={() => handleDelete(gal._id)}
                      className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MOBILE CARD VIEW */}
      <div className="md:hidden grid grid-cols-1 gap-6">
        {gallaries.map((gal) => (
          <div
            key={gal._id}
            className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold text-deep-black mb-2">{gal.gallary_name}</h3>
              <p className="text-gray-600 mb-4">
                Photo: {gal.gallary_photo ? (
                  <a href={`${gal.gallary_photo}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                    View File
                  </a>
                ) : (
                  'No file'
                )}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => handleEdit(gal._id)}
                  className="bg-gold text-deep-black px-4 py-2 rounded-lg font-semibold hover:bg-gold-dark transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <FontAwesomeIcon icon={faPen} className="mr-2" /> Edit
                </button>
                <button
                  onClick={() => handleDelete(gal._id)}
                  className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-600 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <FontAwesomeIcon icon={faTrash} className="mr-2" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* EDIT MODAL */}
      {showEditModal && editingGallary && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-6 text-deep-black">Edit Gallary</h2>
            
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block mb-2 text-deep-black font-semibold">
                  Gallary Name
                </label>
                <input
                  type="text"
                  value={editingGallary.gallary_name}
                  onChange={(e) => setEditingGallary({ ...editingGallary, gallary_name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block mb-2 text-deep-black font-semibold">
                  Gallary Photo ( Image )
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                />
                {file && <p className="text-sm text-gray-600 mt-1">Selected: {file.name}</p>}
                {editingGallary.gallary_photo && !file && (
                  <p className="text-sm text-gray-600 mt-1">
                    Current: <a href={`${editingGallary.gallary_photo}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">View File</a>
                  </p>
                )}
              </div>
              
              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-3 bg-gray-300 rounded-lg hover:bg-gray-400 transition-all duration-200 font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updating}
                  className="px-6 py-3 bg-gold text-deep-black rounded-lg hover:bg-gold-dark transition-all duration-200 font-semibold disabled:opacity-50"
                >
                  {updating ? "Updating..." : "Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CONFIRMATION MODAL */}
      {showConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4 text-deep-black">Confirm Delete</h2>
            <p className="mb-6 text-gray-600">Are you sure you want to delete this gallary?</p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-6 py-3 bg-gray-300 rounded-lg hover:bg-gray-400 transition-all duration-200 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ERROR MODAL */}
      {showError && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4 text-deep-black">Error</h2>
            <p className="mb-6 text-gray-600">{errorMessage}</p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowError(false)}
                className="px-6 py-3 bg-gold text-deep-black rounded-lg hover:bg-gold-dark transition-all duration-200 font-semibold"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}